import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const TeamBuilderModal = ({ isOpen, onClose, onCreateTeam, problemStatements = [] }) => {
  const [teamData, setTeamData] = useState({
    name: '',
    description: '',
    problemStatementId: '',
    lookingForRoles: [],
    preferredSkills: '',
    meetingPreference: 'hybrid',
    timeCommitment: 'part-time'
  });
  const [isCreating, setIsCreating] = useState(false);

  const roleOptions = [
    { value: 'Developer', label: 'Developer' },
    { value: 'Project Manager', label: 'Project Manager' },
    { value: 'Business Analyst', label: 'Business Analyst' },
    { value: 'Product Designer', label: 'Product Designer' }
  ];

  const meetingOptions = [
    { value: 'remote', label: 'Remote Only' },
    { value: 'in-person', label: 'In-Person Only' },
    { value: 'hybrid', label: 'Hybrid (Remote + In-Person)' }
  ];

  const commitmentOptions = [
    { value: 'part-time', label: 'Part-time (10-15 hours/week)' },
    { value: 'full-time', label: 'Full-time (20+ hours/week)' },
    { value: 'flexible', label: 'Flexible Schedule' }
  ];

  const problemOptions = problemStatements?.map(problem => ({
    value: problem?.id,
    label: problem?.title,
    description: problem?.organization
  }));

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsCreating(true);
    
    try {
      await onCreateTeam(teamData);
      onClose();
      setTeamData({
        name: '',
        description: '',
        problemStatementId: '',
        lookingForRoles: [],
        preferredSkills: '',
        meetingPreference: 'hybrid',
        timeCommitment: 'part-time'
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleInputChange = (field, value) => {
    setTeamData(prev => ({ ...prev, [field]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">Create New Team</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Basic Information</h3>
            
            <Input
              label="Team Name"
              type="text"
              placeholder="Enter your team name"
              value={teamData?.name}
              onChange={(e) => handleInputChange('name', e?.target?.value)}
              required
            />

            <Input
              label="Team Description"
              type="text"
              placeholder="Describe your team's vision and goals"
              value={teamData?.description}
              onChange={(e) => handleInputChange('description', e?.target?.value)}
              required
            />
          </div>

          {/* Problem Statement Selection */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Problem Focus</h3>
            
            <Select
              label="Problem Statement (Optional)"
              placeholder="Select a problem statement to work on"
              options={problemOptions}
              value={teamData?.problemStatementId}
              onChange={(value) => handleInputChange('problemStatementId', value)}
              searchable
            />
          </div>

          {/* Team Composition */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Team Composition</h3>
            
            <Select
              label="Looking for Roles"
              description="Select the roles you need for your team"
              options={roleOptions}
              value={teamData?.lookingForRoles}
              onChange={(value) => handleInputChange('lookingForRoles', value)}
              multiple
              required
            />

            <Input
              label="Preferred Skills"
              type="text"
              placeholder="e.g., React, Python, UI/UX Design, Data Analysis"
              description="List specific skills you're looking for in teammates"
              value={teamData?.preferredSkills}
              onChange={(e) => handleInputChange('preferredSkills', e?.target?.value)}
            />
          </div>

          {/* Work Preferences */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Work Preferences</h3>
            
            <Select
              label="Meeting Preference"
              options={meetingOptions}
              value={teamData?.meetingPreference}
              onChange={(value) => handleInputChange('meetingPreference', value)}
            />

            <Select
              label="Time Commitment"
              options={commitmentOptions}
              value={teamData?.timeCommitment}
              onChange={(value) => handleInputChange('timeCommitment', value)}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isCreating}
              iconName="Users"
              iconPosition="left"
              iconSize={16}
            >
              Create Team
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TeamBuilderModal;